import React, { useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth, roleLabel } from "@/context/AuthContext";
import UsersPanel from "@/components/admin/UsersPanel";
import PermissionsPanel from "@/components/admin/PermissionsPanel";
import AuditLogPanel from "@/components/admin/AuditLogPanel";
import ClientOnboardingPanel from "@/components/admin/ClientOnboardingPanel";
import ReorderPanel from "@/components/admin/ReorderPanel";
import { ArrowUpDown } from "lucide-react";
import { RecordTagFields, scopeLabel, orgLookup } from "@/components/admin/ScopeFields";
import { OrgsProvider, useOrgs } from "@/context/OrgsContext";
import { apiError } from "@/lib/errors";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Check, X, Pencil, Trash2, Upload, Plus } from "lucide-react";
import { toast } from "sonner";
import Pagination from "@/components/Pagination";

const PAGE_SIZE = 50;

const TAB = "rounded-none font-heading tracking-wide data-[state=active]:bg-[#0A1128] data-[state=active]:text-white";
export default function DashboardPage() {
  return <OrgsProvider><Dashboard/></OrgsProvider>;
}

function Dashboard() {
  const { user, can, hasAdminAccess } = useAuth();
  const { orgs, reloadOrgs } = useOrgs();
  const [sports, setSports] = useState([]);
  const [pendingRecords, setPendingRecords] = useState([]);
  const [users, setUsers] = useState([]);
  const [settings, setSettings] = useState({ site_title: "Sports Record Book" });
  const [reloadToken, setReloadToken] = useState(0);

  const loadAll = async () => {
    const [s, st] = await Promise.all([api.get("/sports"), api.get("/settings"), reloadOrgs()]);
    setSports(s.data);
    setSettings(st.data);
    if (can("approve_records")) {
      try {
        const p = await api.get("/records", { params: { status: "pending", manage: true, page: 0, page_size: 500 } });
        setPendingRecords(p.data.items || []);
      } catch {}
    }
    if (can("manage_users")) {
      try { const u = await api.get("/users"); setUsers(u.data); } catch {}
    }
  };

  useEffect(() => { if (user) loadAll(); }, [user]);
  const reload = () => { setReloadToken(t => t + 1); loadAll(); };

  if (user === undefined) return <div className="p-8">Loading...</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (!hasAdminAccess) return <Navigate to="/" replace />;

  const isSuper = user.role === "super_admin";
  const scope = scopeLabel(user, orgs);
  const mySports = user.role === "coach" ? sports.filter(s => (user.sport_ids || []).includes(s.id)) : sports;
  const tabs = [
    ["records", "Records", true],
    ["pending", `Pending (${pendingRecords.length})`, can("approve_records")],
    ["submit", "Submit", can("edit_records")],
    ["sports", "Sports", can("manage_sports")],
    ["import", "CSV Import", can("import_records") || can("view_upload_history")],
    ["onboarding", "Client Onboarding", can("manage_organizations") && can("manage_users")],
    ["users", "Users", can("manage_users")],
    ["permissions", "Permissions", isSuper],
    ["audit", "Audit Log", can("view_audit_log")],
    ["settings", "Settings", can("manage_site_settings")],
  ].filter(t => t[2]);

  return (
    <main className="max-w-7xl mx-auto px-6 py-8" data-testid="dashboard-page">
      <div className="mb-6 border-b-2 border-[#0A1128] pb-4">
        <p className="font-overline text-xs text-[#E11D48]" data-testid="dashboard-role">{roleLabel(user.role).toUpperCase()}{scope && ` · ${scope}`}</p>
        <h1 className="font-heading text-4xl tracking-tight">DASHBOARD</h1>
        <p className="text-sm text-slate-600 mt-1">Signed in as <strong>{user.name}</strong> — {user.email}</p>
      </div>

      <Tabs defaultValue="records" className="w-full">
        <TabsList className="rounded-none border-2 border-[#0A1128] bg-white p-0 h-auto flex-wrap">
          {tabs.map(([k, label]) => (
            <TabsTrigger key={k} value={k} className={k === "pending" ? TAB.replace("bg-[#0A1128]", "bg-[#E11D48]") : TAB} data-testid={`tab-${k}`}>{label}</TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="records" className="mt-6">
          <RecordsPanel sports={mySports} reloadToken={reloadToken} onChange={reload} user={user} orgs={orgs}/>
        </TabsContent>
        <TabsContent value="pending" className="mt-6">
          <PendingPanel records={pendingRecords} sports={sports} reload={loadAll} orgs={orgs}/>
        </TabsContent>
        <TabsContent value="submit" className="mt-6">
          <SubmitPanel sports={mySports} reload={loadAll} user={user} orgs={orgs}/>
        </TabsContent>
        <TabsContent value="sports" className="mt-6">
          <SportsPanel sports={sports} reload={loadAll} canEdit={can("manage_sports")} isSuperAdmin={isSuper}/>
        </TabsContent>
        <TabsContent value="import" className="mt-6">
          <ImportPanel sports={mySports} reload={reload} user={user} orgs={orgs}/>
        </TabsContent>
        <TabsContent value="users" className="mt-6">
          <UsersPanel users={users} reload={loadAll} me={user} sports={sports}/>
        </TabsContent>
        <TabsContent value="onboarding" className="mt-6">
          <ClientOnboardingPanel sports={sports} reloadUsers={loadAll}/>
        </TabsContent>
        {isSuper && (
          <TabsContent value="permissions" className="mt-6"><PermissionsPanel/></TabsContent>
        )}
        {can("view_audit_log") && (
          <TabsContent value="audit" className="mt-6"><AuditLogPanel/></TabsContent>
        )}
        <TabsContent value="settings" className="mt-6">
          <SettingsPanel settings={settings} reload={loadAll}/>
        </TabsContent>
      </Tabs>
    </main>
  );
}

// ---------------- Records Panel ----------------
function RecordsPanel({ sports, reloadToken, onChange, user, orgs }) {
  const canEdit = !!user.permissions?.edit_records;
  const canDelete = !!user.permissions?.delete_records;
  const [q, setQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [sportFilter, setSportFilter] = useState("all");
  const [editing, setEditing] = useState(null);
  const [page, setPage] = useState(0);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [rtypesBySport, setRtypesBySport] = useState({}); // sport_id -> [record_type]
  const [rtFilter, setRtFilter] = useState("all");
  const [eventFilter, setEventFilter] = useState("all");
  const [events, setEvents] = useState([]);
  const [reorderMode, setReorderMode] = useState(false);
  const [localReload, setLocalReload] = useState(0);
  const canReorder = !!user.permissions?.reorder_records;
  const sportName = id => sports.find(s => s.id === id)?.name || "—";
  const sportTypes = sportFilter !== "all" ? (rtypesBySport[sportFilter] || []) : [];
  const activeRt = sportTypes.find(t => t.name === rtFilter);
  const grouped = !!(activeRt?.group_by_event_tabs && activeRt?.event_column);
  const reorderReady = sportFilter !== "all" && !!activeRt && (!grouped || eventFilter !== "all");

  useEffect(() => { setRtFilter("all"); }, [sportFilter]);
  useEffect(() => { setEventFilter("all"); setEvents([]); setReorderMode(false); }, [sportFilter, rtFilter]);
  useEffect(() => {
    if (!grouped) return;
    api.get("/records/facets", { params: { sport_id: sportFilter, record_type: activeRt.name, event_column: activeRt.event_column } })
      .then(r => setEvents(r.data.events || [])).catch(() => setEvents([]));
  }, [grouped, sportFilter, activeRt?.name]);

  // Load record types for every sport so we can render each row's schema headers.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const entries = await Promise.all(sports.map(async s => {
        try {
          const { data } = await api.get(`/record-types?sport_id=${s.id}`);
          return [s.id, data];
        } catch { return [s.id, []]; }
      }));
      if (!cancelled) setRtypesBySport(Object.fromEntries(entries));
    })();
    return () => { cancelled = true; };
  }, [sports]);

  // Debounce the search input so we don't hammer the API while typing.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q.trim()), 250);
    return () => clearTimeout(t);
  }, [q]);

  // Reset to first page whenever filter/search inputs change.
  useEffect(() => { setPage(0); }, [debouncedQ, sportFilter, rtFilter, eventFilter]);

  // Server-side load: current page only.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const params = { page, page_size: PAGE_SIZE, manage: true };
    if (sportFilter !== "all") params.sport_id = sportFilter;
    if (debouncedQ) params.search = debouncedQ;
    if (activeRt) params.record_type = activeRt.name;
    if (grouped && eventFilter !== "all") Object.assign(params, { event_column: activeRt.event_column, event_value: eventFilter });
    api.get("/records", { params }).then(res => {
      if (cancelled) return;
      setItems(res.data.items || []);
      setTotal(res.data.total || 0);
    }).catch(() => { if (!cancelled) { setItems([]); setTotal(0); } })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [page, sportFilter, debouncedQ, reloadToken, rtFilter, eventFilter, localReload, activeRt?.name]);

  // Dynamic header set for the current page (union of records' schemas).
  const dynamicColumns = useMemo(() => {
    const seen = new Set();
    const cols = [];
    items.forEach(r => {
      const rt = (rtypesBySport[r.sport_id] || []).find(t => t.name === r.record_type);
      (rt?.columns || []).forEach(c => { if (c && !seen.has(c)) { seen.add(c); cols.push(c); } });
    });
    items.forEach(r => {
      Object.keys(r.dynamic_columns || {}).forEach(c => { if (c && !seen.has(c)) { seen.add(c); cols.push(c); } });
    });
    return cols;
  }, [items, rtypesBySport]);

  return (
    <div className="border border-slate-300 bg-white">
      <div className="p-4 flex flex-col md:flex-row gap-3 border-b items-center">
        <Input placeholder="Search records..." value={q} onChange={e => setQ(e.target.value)} className="rounded-sm max-w-md" data-testid="records-search"/>
        <Select value={sportFilter} onValueChange={setSportFilter}>
          <SelectTrigger className="w-56 rounded-sm" data-testid="records-sport-filter"><SelectValue/></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Sports</SelectItem>
            {sports.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
          </SelectContent>
        </Select>
        {sportFilter !== "all" && (
          <Select value={rtFilter} onValueChange={setRtFilter}>
            <SelectTrigger className="w-56 rounded-sm" data-testid="records-rt-filter"><SelectValue placeholder="All record books"/></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All record books</SelectItem>
              {sportTypes.map(t => <SelectItem key={t.id} value={t.name}>{t.name}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
        {grouped && (
          <Select value={eventFilter} onValueChange={setEventFilter}>
            <SelectTrigger className="w-48 rounded-sm" data-testid="records-event-filter"><SelectValue/></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All events</SelectItem>
              {events.map(ev => <SelectItem key={ev} value={String(ev)}>{String(ev)}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
        {loading && <span className="text-xs text-slate-500 font-overline tracking-wider">Loading…</span>}
        {canReorder && !reorderMode && (
          <Button variant="outline" className="rounded-sm md:ml-auto font-heading tracking-wide" disabled={!reorderReady}
            title={reorderReady ? "Drag & drop to change display order" : (grouped ? "Pick an event to reorder" : "Pick a sport and record book to reorder")}
            onClick={() => setReorderMode(true)} data-testid="reorder-mode-btn">
            <ArrowUpDown className="w-4 h-4 mr-2"/>REORDER MODE
          </Button>
        )}
      </div>
      {canReorder && !reorderReady && !reorderMode && sportFilter !== "all" && (
        <p className="px-4 py-2 text-[11px] text-slate-500 border-b bg-slate-50" data-testid="reorder-hint">
          To reorder, pick a record book{grouped ? " and an event" : ""}.
        </p>
      )}
      {reorderMode ? (
        <ReorderPanel sportId={sportFilter} recordType={activeRt.name} eventColumn={grouped ? activeRt.event_column : null}
          eventValue={grouped ? eventFilter : null} columns={activeRt.columns || []}
          onExit={() => setReorderMode(false)} onSaved={() => setLocalReload(t => t + 1)}/>
      ) : total === 0 ? (
        <div className="p-10 text-center text-slate-500" data-testid="records-empty">
          {loading ? "Loading records..." : (debouncedQ || sportFilter !== "all"
            ? "No records match your filters."
            : "No records yet. Import a CSV to define columns for a record type.")}
        </div>
      ) : (
        <div className="flex flex-col">
          <div className="max-h-[600px] overflow-auto">
            <Table>
              <TableHeader className="bg-slate-100 sticky top-0">
                <TableRow>
                  <TableHead className="font-heading whitespace-nowrap">Sport</TableHead>
                  <TableHead className="font-heading whitespace-nowrap">Record Type</TableHead>
                  <TableHead className="font-heading whitespace-nowrap">Category</TableHead>
                  <TableHead className="font-heading whitespace-nowrap">Association</TableHead>
                  <TableHead className="font-heading whitespace-nowrap">School</TableHead>
                  {dynamicColumns.map(col => (
                    <TableHead key={col} className="font-heading whitespace-nowrap">{col}</TableHead>
                  ))}
                  <TableHead className="font-heading whitespace-nowrap">Status</TableHead>
                  {canEdit && <TableHead>Actions</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map(r => (
                  <TableRow key={r.id} data-testid={`admin-record-${r.id}`}>
                    <TableCell className="text-xs whitespace-nowrap">{sportName(r.sport_id)}</TableCell>
                    <TableCell className="text-xs whitespace-nowrap">{r.record_type || <span className="text-slate-400">—</span>}</TableCell>
                    <TableCell className="text-xs whitespace-nowrap">{r.category}</TableCell>
                    <TableCell className="text-xs whitespace-nowrap" data-testid={`record-association-${r.id}`}>{orgLookup(orgs).association(r.association_id) || "—"}</TableCell>
                    <TableCell className="text-xs whitespace-nowrap" data-testid={`record-school-${r.id}`}>{orgLookup(orgs).school(r.school_id) || <span className="text-slate-400">Association-wide</span>}</TableCell>
                    {dynamicColumns.map(col => {
                      const v = r.dynamic_columns?.[col];
                      return (
                        <TableCell key={col} className="text-sm whitespace-nowrap">
                          {v == null || v === "" ? <span className="text-slate-300">—</span> : v}
                        </TableCell>
                      );
                    })}
                    <TableCell>
                      {r.status === "approved"
                        ? <Badge className="bg-green-50 text-green-700 border border-green-300 rounded-sm">APPROVED</Badge>
                        : <Badge className="bg-red-50 text-red-700 border border-red-300 rounded-sm">PENDING</Badge>}
                    </TableCell>
                    {canEdit && (
                      <TableCell className="flex gap-1">
                        <Button size="sm" variant="outline" className="rounded-sm h-8 w-8 p-0" onClick={() => setEditing(r)} data-testid={`edit-${r.id}`}><Pencil className="w-3 h-3"/></Button>
                        {canDelete && <Button size="sm" variant="outline" className="rounded-sm h-8 w-8 p-0 text-red-600" onClick={async () => {
                          if (!window.confirm("Delete this record?")) return;
                          try { await api.delete(`/records/${r.id}`); toast.success("Deleted"); onChange(); }
                          catch (e) { toast.error(apiError(e)); }
                        }} data-testid={`delete-${r.id}`}><Trash2 className="w-3 h-3"/></Button>}
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <Pagination
            page={page}
            pageSize={PAGE_SIZE}
            total={total}
            onPageChange={setPage}
            testidPrefix="admin-records-pagination"
          />
        </div>
      )}
      {editing && <EditRecordDialog record={editing} sports={sports} user={user} orgs={orgs} onClose={() => setEditing(null)} reload={onChange}/>}
    </div>
  );
}

function EditRecordDialog({ record, sports, user, orgs, onClose, reload }) {
  const canApprove = !!user.permissions?.approve_records;
  const [owner, setOwner] = useState({ association_id: record.association_id || "", school_id: record.school_id || "" });
  // The record type's `columns` array is the single source of truth for what to show.
  // No hardcoded defaults, no legacy fallbacks — the form perfectly matches the CSV
  // schema for this record type. Load asynchronously; render nothing until we have it.
  const [columns, setColumns] = useState(null); // null = loading, [] = no schema yet
  const [dyn, setDyn] = useState({});
  const [status, setStatus] = useState(record.status || "approved");
  const [saving, setSaving] = useState(false);
  const sport = sports.find(s => s.id === record.sport_id);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      let cols = [];
      if (record.sport_id) {
        try {
          const { data } = await api.get(`/record-types?sport_id=${record.sport_id}`);
          const rt = data.find(t => t.name === record.record_type);
          cols = (rt?.columns || []).filter(Boolean);
        } catch { /* ignore */ }
      }
      // Include any keys present on this record that aren't yet in the record type schema
      // (defensive — e.g. if a column was later renamed on the type but the record still holds it).
      const extras = Object.keys(record.dynamic_columns || {}).filter(k => !cols.includes(k));
      const finalCols = [...cols, ...extras];
      if (cancelled) return;
      setColumns(finalCols);
      setDyn(Object.fromEntries(finalCols.map(c => [c, record.dynamic_columns?.[c] ?? ""])));
    })();
    return () => { cancelled = true; };
  }, [record.id, record.sport_id, record.record_type]);

  const save = async () => {
    if (!columns) return;
    setSaving(true);
    try {
      await api.patch(`/records/${record.id}`, {
        dynamic_columns: Object.fromEntries(columns.map(c => [c, dyn[c] ?? ""])),
        category: record.category,
        ...(canApprove ? { status } : {}),
        ...(user.role === "super_admin" ? owner : {}),
      });
      toast.success(canApprove ? "Record updated" : "Changes submitted for approval");
      reload();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="rounded-none border-2 border-[#0A1128] max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="edit-record-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">EDIT RECORD</DialogTitle>
          <p className="font-overline text-[10px] text-slate-500 mt-1">
            {sport?.name || "—"} · {record.record_type || "General"} · {record.category || "—"}
          </p>
        </DialogHeader>

        <div className="space-y-3">
          {columns === null ? (
            <p className="text-sm text-slate-500 italic">Loading schema…</p>
          ) : columns.length === 0 ? (
            <div className="text-sm text-slate-600 border-l-4 border-[#FACC15] pl-3 py-2 bg-yellow-50/40">
              This record type has no columns defined yet. Import a CSV for
              <strong> {record.record_type || "this record type"}</strong> first — the CSV headers become the fields shown here.
            </div>
          ) : (
            <>
              <div className="text-xs text-slate-500 border-l-4 border-[#FACC15] pl-3 py-1">
                Fields below match the CSV columns imported for <strong>{record.record_type}</strong>.
              </div>
              {columns.map(col => (
                <div key={col}>
                  <Label className="font-mono text-xs">{col}</Label>
                  <Input
                    value={dyn[col] ?? ""}
                    onChange={e => setDyn({ ...dyn, [col]: e.target.value })}
                    className="rounded-sm"
                    data-testid={`edit-dyn-${col}`}
                  />
                </div>
              ))}
            </>
          )}

          <div className="border-t pt-3">
            <p className="font-overline text-[10px] text-slate-500 mb-2">Ownership</p>
            <RecordTagFields user={user} value={owner} onChange={setOwner} testidPrefix="edit-owner"/>
          </div>

          {canApprove ? <div>
            <Label>Status</Label>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger className="rounded-sm" data-testid="edit-status"><SelectValue/></SelectTrigger>
              <SelectContent>
                <SelectItem value="approved">Approved</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
              </SelectContent>
            </Select>
          </div> : (
            <p className="text-xs text-slate-600 border-l-4 border-[#E11D48] pl-3" data-testid="edit-pending-notice">Your edits will be sent for approval before going live.</p>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-sm">Cancel</Button>
          <Button
            onClick={save}
            disabled={saving || columns === null || columns.length === 0}
            className="rounded-sm bg-[#0A1128] hover:bg-slate-800"
            data-testid="save-record"
          >
            {saving ? "Saving..." : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------- Pending Panel ----------------
function PendingPanel({ records, sports, reload, orgs }) {
  const [rtypesBySport, setRtypesBySport] = useState({});
  const sportName = id => sports.find(s => s.id === id)?.name || "—";

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const entries = await Promise.all(sports.map(async s => {
        try { const { data } = await api.get(`/record-types?sport_id=${s.id}`); return [s.id, data]; }
        catch { return [s.id, []]; }
      }));
      if (!cancelled) setRtypesBySport(Object.fromEntries(entries));
    })();
    return () => { cancelled = true; };
  }, [sports]);

  const approve = async (r) => {
    try { await api.patch(`/records/${r.id}`, { status: "approved" }); toast.success("Approved"); reload(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const reject = async (r) => {
    if (!confirm("Reject and delete this submission?")) return;
    try { await api.delete(`/records/${r.id}`); toast.success("Rejected"); reload(); }
    catch (e) { toast.error(apiError(e)); }
  };

  if (records.length === 0) return <div className="p-12 text-center text-slate-500 border-2 border-dashed">No pending submissions.</div>;

  const dynamicColumns = (() => {
    const seen = new Set();
    const cols = [];
    records.forEach(r => {
      const rt = (rtypesBySport[r.sport_id] || []).find(t => t.name === r.record_type);
      (rt?.columns || []).forEach(c => { if (c && !seen.has(c)) { seen.add(c); cols.push(c); } });
    });
    records.forEach(r => {
      Object.keys(r.dynamic_columns || {}).forEach(c => { if (c && !seen.has(c)) { seen.add(c); cols.push(c); } });
    });
    return cols;
  })();

  return (
    <div className="border border-slate-300 bg-white overflow-x-auto">
      <Table>
        <TableHeader className="bg-slate-100">
          <TableRow>
            <TableHead className="whitespace-nowrap">Sport</TableHead>
            <TableHead className="whitespace-nowrap">Record Type</TableHead>
            <TableHead className="whitespace-nowrap">Category</TableHead>
            {dynamicColumns.map(col => (
              <TableHead key={col} className="whitespace-nowrap">{col}</TableHead>
            ))}
            <TableHead className="whitespace-nowrap">Owner</TableHead>
            <TableHead className="whitespace-nowrap">Submitted By</TableHead>
            <TableHead>Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {records.map(r => (
            <TableRow key={r.id} data-testid={`pending-${r.id}`}>
              <TableCell className="whitespace-nowrap">{sportName(r.sport_id)}</TableCell>
              <TableCell className="whitespace-nowrap">{r.record_type || <span className="text-slate-400">—</span>}</TableCell>
              <TableCell className="whitespace-nowrap">{r.category}</TableCell>
              {dynamicColumns.map(col => {
                const v = r.dynamic_columns?.[col];
                return (
                  <TableCell key={col} className="text-sm whitespace-nowrap">
                    {v == null || v === "" ? <span className="text-slate-300">—</span> : v}
                  </TableCell>
                );
              })}
              <TableCell className="text-xs whitespace-nowrap">{scopeLabel(r, orgs) || "—"}</TableCell>
              <TableCell className="text-xs">{r.submitted_by_name}</TableCell>
              <TableCell className="flex gap-2">
                <Button size="sm" onClick={() => approve(r)} className="rounded-sm bg-green-600 hover:bg-green-700" data-testid={`approve-${r.id}`}><Check className="w-3 h-3 mr-1"/>Approve</Button>
                <Button size="sm" variant="outline" onClick={() => reject(r)} className="rounded-sm text-red-600 border-red-300" data-testid={`reject-${r.id}`}><X className="w-3 h-3 mr-1"/>Reject</Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

// ---------------- Submit Panel ----------------
function SubmitPanel({ sports, reload, user, orgs }) {
  const canApprove = !!user.permissions?.approve_records;
  const [owner, setOwner] = useState({ association_id: orgs.default_association_id || "", school_id: "" });
  const [sportId, setSportId] = useState("");
  const [recordType, setRecordType] = useState("");
  const [category, setCategory] = useState("");
  const [newCat, setNewCat] = useState("");
  const [dyn, setDyn] = useState({});
  const [rtypes, setRtypes] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const sport = sports.find(s => s.id === sportId);
  const cats = sport?.categories || [];

  useEffect(() => {
    if (!sportId) { setRtypes([]); return; }
    api.get(`/record-types?sport_id=${sportId}`).then(r => setRtypes(r.data)).catch(() => setRtypes([]));
  }, [sportId]);

  const activeRt = rtypes.find(t => t.name === recordType);
  const columns = (activeRt?.columns || []).filter(Boolean);

  const submit = async (e) => {
    e.preventDefault();
    const finalCategory = category === "__new__" ? newCat.trim() : category;
    if (!sportId) { toast.error("Please select a sport"); return; }
    if (!recordType) { toast.error("Please select a record type"); return; }
    if (columns.length === 0) { toast.error("This record type has no columns yet — an admin must import a CSV first."); return; }
    if (!finalCategory) { toast.error("Please select or add a category"); return; }
    setSubmitting(true);
    try {
      await api.post("/records", {
        sport_id: sportId,
        record_type: recordType,
        category: finalCategory,
        dynamic_columns: Object.fromEntries(columns.map(c => [c, dyn[c] ?? ""])),
        ...(user.role === "super_admin" ? { association_id: owner.association_id || orgs.default_association_id, school_id: owner.school_id || null } : {}),
      });
      toast.success(canApprove ? "Record added" : "Submitted for approval");
      setSportId(""); setRecordType(""); setCategory(""); setNewCat(""); setDyn({});
      reload();
    } catch (err) {
      toast.error(apiError(err, "Failed to submit"));
    } finally { setSubmitting(false); }
  };

  return (
    <form onSubmit={submit} className="border border-slate-300 bg-white p-6 max-w-2xl space-y-4" data-testid="submit-form">
      <h3 className="font-heading text-2xl">SUBMIT A RECORD</h3>
      <div>
        <Label>Sport *</Label>
        <Select value={sportId} onValueChange={v => { setSportId(v); setRecordType(""); setCategory(""); setDyn({}); }}>
          <SelectTrigger className="rounded-sm" data-testid="submit-sport"><SelectValue placeholder="Select a sport"/></SelectTrigger>
          <SelectContent>{sports.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      {sportId && (
        <RecordTagFields user={user} value={owner} onChange={setOwner} testidPrefix="submit-owner"/>
      )}
      {sportId && (
        <div>
          <Label>Record Type *</Label>
          <Select value={recordType} onValueChange={v => { setRecordType(v); setDyn({}); }}>
            <SelectTrigger className="rounded-sm" data-testid="submit-record-type"><SelectValue placeholder="Select a record type"/></SelectTrigger>
            <SelectContent>
              {rtypes.length === 0
                ? <SelectItem value="__none__" disabled>No record types yet</SelectItem>
                : rtypes.map(t => <SelectItem key={t.id} value={t.name}>{t.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      )}
      {recordType && (
        <div>
          <Label>Category *</Label>
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger className="rounded-sm" data-testid="submit-category"><SelectValue placeholder="Select a category"/></SelectTrigger>
            <SelectContent>
              {cats.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              <SelectItem value="__new__">+ Add new category</SelectItem>
            </SelectContent>
          </Select>
          {category === "__new__" && (
            <Input placeholder="New category name" value={newCat} onChange={e => setNewCat(e.target.value)} className="rounded-sm mt-2" data-testid="submit-new-cat"/>
          )}
        </div>
      )}
      {recordType && (
        columns.length === 0 ? (
          <div className="text-sm text-slate-600 border-l-4 border-[#FACC15] pl-3 py-2 bg-yellow-50/40">
            <strong>{recordType}</strong> has no columns defined yet. An admin must import a CSV for this record type before you can submit.
          </div>
        ) : (
          <div className="border-t pt-3 space-y-3">
            <p className="font-overline text-[10px] text-slate-500">Fields for {recordType}</p>
            {columns.map(col => (
              <div key={col}>
                <Label className="font-mono text-xs">{col}</Label>
                <Input value={dyn[col] ?? ""} onChange={e => setDyn({ ...dyn, [col]: e.target.value })} className="rounded-sm" data-testid={`submit-dyn-${col}`}/>
              </div>
            ))}
          </div>
        )
      )}
      <Button
        type="submit"
        disabled={submitting || !recordType || columns.length === 0}
        className="bg-[#0A1128] hover:bg-slate-800 rounded-sm font-heading"
        data-testid="submit-record-btn"
      >
        {submitting ? "SUBMITTING..." : (canApprove ? "ADD RECORD" : "SUBMIT FOR APPROVAL")}
      </Button>
    </form>
  );
}

// ---------------- Sports Panel ----------------
function SportsPanel({ sports, reload, canEdit, isSuperAdmin }) {
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState({ name: "", image_url: "", description: "" });
  const [catInputs, setCatInputs] = useState({});

  const create = async () => {
    try { await api.post("/sports", form); toast.success("Sport added"); setShowNew(false); setForm({ name: "", image_url: "", description: "" }); reload(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const addCat = async (sportId) => {
    const name = (catInputs[sportId] || "").trim();
    if (!name) return;
    await api.post(`/sports/${sportId}/categories`, { name });
    setCatInputs({ ...catInputs, [sportId]: "" });
    toast.success("Category added"); reload();
  };
  const removeCat = async (sportId, name) => {
    await api.delete(`/sports/${sportId}/categories/${encodeURIComponent(name)}`);
    toast.success("Category removed"); reload();
  };
  const deleteSport = async (id) => {
    if (!confirm("Delete this sport AND all its records?")) return;
    await api.delete(`/sports/${id}`); toast.success("Deleted"); reload();
  };

  return (
    <div className="space-y-4">
      {canEdit && (
        <div className="flex justify-end">
          <Button onClick={() => setShowNew(!showNew)} className="rounded-sm bg-[#0A1128] hover:bg-slate-800" data-testid="add-sport-btn"><Plus className="w-4 h-4 mr-1"/>Add Sport</Button>
        </div>
      )}
      {showNew && (
        <div className="border-2 border-[#0A1128] bg-white p-4 space-y-2" data-testid="new-sport-form">
          <Input placeholder="Sport name" value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="rounded-sm" data-testid="new-sport-name"/>
          <Input placeholder="Image URL (optional)" value={form.image_url} onChange={e => setForm({...form, image_url: e.target.value})} className="rounded-sm"/>
          <Textarea placeholder="Description (optional)" value={form.description} onChange={e => setForm({...form, description: e.target.value})} className="rounded-sm"/>
          <Button onClick={create} className="rounded-sm bg-[#0A1128] hover:bg-slate-800" data-testid="save-new-sport">Create Sport</Button>
        </div>
      )}
      {sports.map(s => (
        <div key={s.id} className="border border-slate-300 bg-white p-4" data-testid={`sport-manage-${s.slug}`}>
          <div className="flex justify-between items-start">
            <div>
              <h3 className="font-heading text-xl">{s.name}</h3>
              <p className="text-xs text-slate-500">{s.record_count} records · {s.categories?.length || 0} categories</p>
            </div>
            {isSuperAdmin && <Button variant="outline" size="sm" onClick={() => deleteSport(s.id)} className="rounded-sm text-red-600 border-red-300" data-testid={`del-sport-${s.slug}`}><Trash2 className="w-3 h-3"/></Button>}
          </div>
          {canEdit && (
            <div className="mt-3">
              <p className="text-xs font-overline text-slate-500 mb-1">Categories</p>
              <div className="flex flex-wrap gap-2 mb-2">
                {(s.categories || []).map(c => (
                  <span key={c} className="bg-slate-100 border border-slate-300 px-2 py-1 text-xs flex items-center gap-1">
                    {c}
                    {isSuperAdmin && <button onClick={() => removeCat(s.id, c)} className="text-red-600 hover:text-red-800"><X className="w-3 h-3"/></button>}
                  </span>
                ))}
              </div>
              <div className="flex gap-2">
                <Input placeholder="New category" value={catInputs[s.id] || ""} onChange={e => setCatInputs({...catInputs, [s.id]: e.target.value})} className="rounded-sm max-w-xs h-8 text-sm" data-testid={`cat-input-${s.slug}`}/>
                <Button size="sm" onClick={() => addCat(s.id)} className="rounded-sm bg-[#0A1128] hover:bg-slate-800 h-8" data-testid={`add-cat-${s.slug}`}>Add</Button>
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// ---------------- Import Panel (fully dynamic, no column mapping) ----------------
function ImportPanel({ sports, reload, user, orgs }) {
  const canImport_ = !!user.permissions?.import_records;
  const canHistory = !!user.permissions?.view_upload_history;
  const [owner, setOwner] = useState({ association_id: orgs.default_association_id || "", school_id: "" });
  useEffect(() => {
    if (!owner.association_id && orgs.default_association_id) setOwner(o => ({ ...o, association_id: orgs.default_association_id }));
  }, [orgs.default_association_id]);
  const [sportId, setSportId] = useState("");
  const [recordType, setRecordType] = useState("");
  const [newRtName, setNewRtName] = useState("");
  const [category, setCategory] = useState("General");
  const [file, setFile] = useState(null);
  const [headers, setHeaders] = useState([]);
  const [previewRows, setPreviewRows] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [sportRecordTypes, setSportRecordTypes] = useState([]);
  const [importing, setImporting] = useState(false);
  const [batches, setBatches] = useState([]);

  const loadBatches = async () => {
    if (!canHistory) return;
    try {
      const url = sportId ? `/import-batches?sport_id=${sportId}` : "/import-batches";
      const { data } = await api.get(url);
      setBatches(data || []);
    } catch { setBatches([]); }
  };

  useEffect(() => {
    if (!sportId) { setSportRecordTypes([]); setBatches([]); return; }
    api.get(`/record-types?sport_id=${sportId}`).then(r => setSportRecordTypes(r.data)).catch(() => {});
    loadBatches();
  }, [sportId]);

  const onFile = async (f) => {
    setFile(f); setHeaders([]); setPreviewRows([]); setTotalRows(0);
    if (!f) return;
    const fd = new FormData(); fd.append("file", f);
    try {
      const { data } = await api.post("/records/import/preview", fd);
      const seen = new Set();
      const clean = (data.headers || []).filter(h => {
        if (!h || !String(h).trim()) return false;
        if (seen.has(h)) return false;
        seen.add(h); return true;
      });
      setHeaders(clean);
      setPreviewRows(data.rows || []);
      setTotalRows(data.total_previewed || (data.rows || []).length);
    } catch (e) {
      toast.error("Failed to read CSV. Ensure it's a valid CSV with a header row.");
    }
  };

  const effectiveRecordType = (recordType === "__new__" ? newRtName : recordType).trim();
  const canImport = !!sportId && !!effectiveRecordType && !!file && headers.length > 0;

  const submitImport = async () => {
    setImporting(true);
    const fd = new FormData();
    fd.append("sport_id", sportId);
    fd.append("record_type", effectiveRecordType);
    fd.append("category", category || "General");
    if (user.role === "super_admin") {
      if (!owner.association_id) { toast.error("Select a target association"); setImporting(false); return; }
      fd.append("association_id", owner.association_id); fd.append("school_id", owner.school_id || "");
    }
    fd.append("file", file);
    try {
      const { data } = await api.post("/records/import/dynamic", fd);
      toast.success(`Imported ${data.imported} rows into "${effectiveRecordType}" with ${data.columns.length} columns`);
      setFile(null); setHeaders([]); setPreviewRows([]); setTotalRows(0);
      setNewRtName(""); setRecordType(effectiveRecordType);
      await loadBatches();
      reload();
    } catch (e) {
      toast.error(apiError(e, "Import failed"));
    } finally {
      setImporting(false);
    }
  };

  const deleteBatch = async (b) => {
    if (!confirm(`Delete this upload? This will permanently remove ${b.count} record${b.count === 1 ? "" : "s"} imported from "${b.filename}".`)) return;
    try {
      const { data } = await api.delete(`/import-batches/${b.id}`);
      toast.success(`Deleted ${data.deleted_records} record(s) from upload.`);
      await loadBatches();
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to delete upload");
    }
  };

  return (
    <div className="space-y-6" data-testid="import-form">
      {canImport_ && <div className="border border-slate-300 bg-white p-6 space-y-5">
        <div className="flex items-baseline justify-between">
          <h3 className="font-heading text-2xl">CSV IMPORT</h3>
          <p className="text-xs text-slate-500">Column headers in your CSV become the fields shown on the public page — no mapping required.</p>
        </div>

        <div className="border-l-4 border-[#0A1128] bg-slate-50 p-4 space-y-2" data-testid="import-target">
          <p className="font-overline text-[10px] text-slate-600">Target — every record in this upload is tagged with</p>
          <RecordTagFields user={user} value={owner} onChange={setOwner} testidPrefix="import-owner"/>
        </div>


        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <Label>Sport *</Label>
            <Select value={sportId} onValueChange={setSportId}>
              <SelectTrigger className="rounded-sm" data-testid="import-sport"><SelectValue placeholder="Select sport"/></SelectTrigger>
              <SelectContent>{sports.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Record Type *</Label>
            <Select value={recordType} onValueChange={setRecordType} disabled={!sportId}>
              <SelectTrigger className="rounded-sm" data-testid="import-record-type"><SelectValue placeholder="Pick or create..."/></SelectTrigger>
              <SelectContent>
                {sportRecordTypes.map(t => <SelectItem key={t.id} value={t.name}>{t.name}</SelectItem>)}
                <SelectItem value="__new__">+ Create new record type</SelectItem>
              </SelectContent>
            </Select>
            {recordType === "__new__" && (
              <Input
                placeholder="New record type name (e.g. Rushing Records)"
                value={newRtName}
                onChange={e => setNewRtName(e.target.value)}
                className="rounded-sm mt-2 h-9"
                data-testid="import-new-rt-name"
              />
            )}
          </div>
          <div>
            <Label>Category</Label>
            <Input value={category} onChange={e => setCategory(e.target.value)} className="rounded-sm" data-testid="import-category"/>
          </div>
        </div>


        <div>
          <Label>CSV File *</Label>
          <Input type="file" accept=".csv" onChange={e => onFile(e.target.files?.[0])} className="rounded-sm" data-testid="import-file"/>
          {headers.length > 0 && (
            <p className="text-xs text-green-700 mt-1">
              Detected <strong>{headers.length}</strong> column{headers.length === 1 ? "" : "s"} and <strong>{totalRows}</strong> row{totalRows === 1 ? "" : "s"}.
            </p>
          )}
        </div>

        {headers.length > 0 && (
          <div className="space-y-2">
            <p className="font-overline text-[10px] text-slate-500">Preview — this is exactly how the data will be stored & displayed</p>
            <div className="border border-slate-200 max-h-80 overflow-auto" data-testid="import-preview">
              <Table>
                <TableHeader className="bg-slate-100 sticky top-0">
                  <TableRow>
                    {headers.map(h => <TableHead key={h} className="text-xs whitespace-nowrap">{h}</TableHead>)}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {previewRows.slice(0, 10).map((r, i) => (
                    <TableRow key={i} data-testid={`preview-row-${i}`}>
                      {headers.map(h => <TableCell key={h} className="text-xs whitespace-nowrap">{r[h] ?? ""}</TableCell>)}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            <p className="text-[10px] text-slate-500">Showing first {Math.min(10, previewRows.length)} of {totalRows}.</p>
          </div>
        )}

        <div className="flex justify-end">
          <Button
            disabled={!canImport || importing}
            onClick={submitImport}
            className="bg-[#0A1128] hover:bg-slate-800 rounded-sm font-heading"
            data-testid="import-submit"
          >
            <Upload className="w-4 h-4 mr-2"/>
            {importing ? "Importing..." : `Confirm & Import ${totalRows} row${totalRows === 1 ? "" : "s"}`}
          </Button>
        </div>
      </div>}

      {/* Upload History */}
      {canHistory && <div className="border border-slate-300 bg-white p-6 space-y-3" data-testid="import-history">
        <div className="flex items-baseline justify-between">
          <h3 className="font-heading text-2xl">UPLOAD HISTORY</h3>
          <p className="text-xs text-slate-500">
            {sportId ? "Showing uploads for the selected sport." : "Pick a sport above to filter, or view all recent uploads below."}
          </p>
        </div>
        {batches.length === 0 ? (
          <p className="text-sm text-slate-500 italic">No uploads yet.</p>
        ) : (
          <div className="border border-slate-200 overflow-auto">
            <Table>
              <TableHeader className="bg-slate-100">
                <TableRow>
                  <TableHead className="text-xs">File</TableHead>
                  <TableHead className="text-xs">Sport</TableHead>
                  <TableHead className="text-xs">Record Type</TableHead>
                  <TableHead className="text-xs">Category</TableHead>
                  <TableHead className="text-xs">Rows</TableHead>
                  <TableHead className="text-xs">Uploaded</TableHead>
                  <TableHead className="text-xs w-20"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {batches.map(b => {
                  const sport = sports.find(s => s.id === b.sport_id);
                  const when = b.uploaded_at ? new Date(b.uploaded_at).toLocaleString() : "";
                  return (
                    <TableRow key={b.id} data-testid={`batch-row-${b.id}`}>
                      <TableCell className="text-xs font-mono">{b.filename}</TableCell>
                      <TableCell className="text-xs">{sport?.name || b.sport_id}</TableCell>
                      <TableCell className="text-xs">{b.record_type}</TableCell>
                      <TableCell className="text-xs">{b.category}</TableCell>
                      <TableCell className="text-xs font-stat">{b.count}</TableCell>
                      <TableCell className="text-xs text-slate-500">{when}<br/><span className="text-[10px]">by {b.uploaded_by_name || "—"}</span></TableCell>
                      <TableCell>
                        <Button
                          variant="outline"
                          size="sm"
                          className="rounded-sm text-red-600 border-red-300 hover:bg-red-50"
                          onClick={() => deleteBatch(b)}
                          data-testid={`delete-batch-${b.id}`}
                        >
                          <Trash2 className="w-3.5 h-3.5 mr-1"/>Delete
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </div>}
    </div>
  );
}

// ---------------- Settings Panel ----------------
function SettingsPanel({ settings, reload }) {
  const [title, setTitle] = useState(settings.site_title || "Sports Record Book");
  useEffect(() => setTitle(settings.site_title || "Sports Record Book"), [settings]);
  const save = async () => {
    await api.patch("/settings", { site_title: title });
    toast.success("Settings updated"); reload();
  };
  return (
    <div className="border border-slate-300 bg-white p-6 max-w-lg space-y-4" data-testid="settings-panel">
      <h3 className="font-heading text-2xl">SITE SETTINGS</h3>
      <div>
        <Label>Site Title (displayed in header)</Label>
        <Input value={title} onChange={e => setTitle(e.target.value)} className="rounded-sm" data-testid="site-title-input"/>
      </div>
      <Button onClick={save} className="bg-[#0A1128] hover:bg-slate-800 rounded-sm font-heading" data-testid="save-settings">SAVE</Button>
    </div>
  );
}
